import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Save, Settings } from "lucide-react";

const Admin = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/");
  }, [loading, user, navigate]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-guides"],
    queryFn: async () => (await api.get("/admin/guides")).data,
    enabled: !!user?.is_admin,
  });

  const guides = data?.guides || [];

  const openGuide = (g) => {
    setSelected(g.slug);
    setDraft(JSON.parse(JSON.stringify(g)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/guides/${selected}`, {
        tagline: draft.tagline,
        teaser: draft.teaser,
        price: parseFloat(draft.price),
        published: draft.published,
        last_updated: draft.last_updated,
        sections: draft.sections,
      });
      toast.success("Guide saved & published");
      await refetch();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user?.is_admin) return <div className="min-h-screen flex justify-center items-center bg-[#F9F9F7]"><Loader2 className="w-8 h-8 animate-spin text-[#9A3412]" /></div>;

  return (
    <div className="bg-[#F9F9F7] min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14">
        <div className="flex items-center gap-3 mb-10">
          <Settings className="w-6 h-6 text-[#9A3412]" />
          <h1 className="font-serif text-4xl font-medium text-stone-900">Admin CMS</h1>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#9A3412]" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Guide list */}
            <div className="lg:col-span-1 space-y-2">
              {guides.map((g) => (
                <button
                  key={g.slug}
                  data-testid={`admin-guide-${g.slug}`}
                  onClick={() => openGuide(g)}
                  className={`w-full text-left px-4 py-3 border transition-colors flex items-center justify-between ${selected === g.slug ? "border-[#9A3412] bg-white" : "border-stone-200 bg-white hover:border-stone-400"}`}
                >
                  <span className="flex items-center gap-2"><span>{g.flag}</span><span className="font-medium text-stone-800">{g.country}</span></span>
                  <span className={`text-xs px-2 py-0.5 ${g.published ? "text-[#166534]" : "text-stone-400"}`}>{g.published ? "Live" : "Draft"}</span>
                </button>
              ))}
            </div>

            {/* Editor */}
            <div className="lg:col-span-3">
              {!draft ? (
                <div className="border border-dashed border-stone-300 bg-white py-24 text-center text-stone-500">Select a guide to edit its content, price, and publish status.</div>
              ) : (
                <div className="border border-stone-200 bg-white p-8 space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="font-serif text-3xl text-stone-900">{draft.flag} {draft.country}</h2>
                    <div className="flex items-center gap-3">
                      <Label htmlFor="pub" className="text-sm">Published</Label>
                      <Switch id="pub" data-testid="admin-published-switch" checked={draft.published} onCheckedChange={(v) => setDraft({ ...draft, published: v })} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm mb-1 block">Price (USD)</Label>
                      <Input data-testid="admin-price" type="number" step="0.01" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} className="rounded-none" />
                    </div>
                    <div>
                      <Label className="text-sm mb-1 block">Last updated</Label>
                      <Input data-testid="admin-last-updated" value={draft.last_updated} onChange={(e) => setDraft({ ...draft, last_updated: e.target.value })} className="rounded-none" placeholder="2026-06-01" />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm mb-1 block">Tagline</Label>
                    <Input data-testid="admin-tagline" value={draft.tagline} onChange={(e) => setDraft({ ...draft, tagline: e.target.value })} className="rounded-none" />
                  </div>
                  <div>
                    <Label className="text-sm mb-1 block">Teaser (free preview intro)</Label>
                    <Textarea data-testid="admin-teaser" value={draft.teaser} onChange={(e) => setDraft({ ...draft, teaser: e.target.value })} rows={3} className="rounded-none" />
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-serif text-2xl text-stone-900 border-t border-stone-200 pt-6">Sections</h3>
                    {draft.sections.map((s, i) => (
                      <div key={i} className="space-y-2">
                        <Input
                          data-testid={`admin-section-title-${i}`}
                          value={s.title}
                          onChange={(e) => {
                            const sec = [...draft.sections];
                            sec[i] = { ...sec[i], title: e.target.value };
                            setDraft({ ...draft, sections: sec });
                          }}
                          className="rounded-none font-medium"
                        />
                        <Textarea
                          data-testid={`admin-section-content-${i}`}
                          value={s.content}
                          onChange={(e) => {
                            const sec = [...draft.sections];
                            sec[i] = { ...sec[i], content: e.target.value };
                            setDraft({ ...draft, sections: sec });
                          }}
                          rows={5}
                          className="rounded-none font-reader"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end border-t border-stone-200 pt-6">
                    <Button data-testid="admin-save-btn" onClick={save} disabled={saving} className="bg-[#9A3412] hover:bg-[#7C2D12] text-white rounded-none px-8">
                      {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-2" />Save & publish</>}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
